// La lógica pura de «Inversión sin control» (la solapa `/alerts/sin-control`, del Centro de Alertas o sola):
// filtros, conteos, qué acción ofrece cada ítem y cómo se descuenta del total lo que el usuario ya
// resolvió en pantalla. Sin React ni red: se testea sola (`__tests__/uncontrolledModel.test.js`).
//
// El informe lo arma el backend (`UncontrolledSpendService`). Cada ítem de un cliente es una cuenta:
// - `NO_LINE`: sin línea ni límite diario, con gasto;
// - `UNCOVERED`: campañas con gasto que ninguna línea mira, en una cuenta que sí tiene línea. Puede
//   traer `stale_lines` (la ADVERTENCIA: una línea de esa cuenta no gastó en 7 días) y `limit_only`
//   (la cuenta tiene límite diario: va al segundo total, no al rojo). Desde UD-5 trae `lines`: TODAS las
//   líneas de la cuenta que quien mira puede ver (`{id, name, bucc_id, client_id, client_name,
//   accepts_campaigns}`, sin las eventuales terminadas); el botón principal decide con ellas.

import {normalizeText, textIncludes} from '../../../utils/textNormalize';

export const KIND_NO_LINE = 'NO_LINE';
export const KIND_UNCOVERED = 'UNCOVERED';

export const FILTER_ALL = 'ALL';
export const FILTER_NO_LINE = 'NO_LINE';
export const FILTER_UNCOVERED = 'UNCOVERED';
export const FILTER_STALE = 'STALE';
export const FILTERS = [FILTER_ALL, FILTER_NO_LINE, FILTER_UNCOVERED, FILTER_STALE];

export const ACTION_CREATE_LINE = 'CREATE_LINE';
export const ACTION_ADD_TO_LINE = 'ADD_TO_LINE';
// Varias líneas de lista: «Sumar las N a una línea» despliega «¿A qué línea?».
export const ACTION_CHOOSE_LINE = 'CHOOSE_LINE';
export const ACTION_EDIT_LINE = 'EDIT_LINE';

// Con MÁS de estas líneas de lista, «¿A qué línea?» suma un buscador.
export const SEARCH_FROM_LINES = 8;

// Los motivos de «No hace falta controlarla», en el orden del mockup (el primero va elegido).
export const DISMISS_REASONS = ['TEST_ACCOUNT', 'CONTROLLED_ELSEWHERE', 'ENDING_SOON'];

export function filterFromParam(value) {
    return FILTERS.includes(value) ? value : FILTER_ALL;
}

/** La clave estable de un ítem: un cliente no tiene dos veces la misma cuenta. */
export function itemKey(clientId, item) {
    return `${clientId}|${item.channel}|${item.account_id}`;
}

export function isStale(item) {
    return Array.isArray(item.stale_lines) && item.stale_lines.length > 0;
}

function matchesFilter(item, filter) {
    if (filter === FILTER_NO_LINE) return item.kind === KIND_NO_LINE;
    if (filter === FILTER_UNCOVERED) return item.kind === KIND_UNCOVERED;
    if (filter === FILTER_STALE) return isStale(item);
    return true;
}

function allItems(report) {
    return ((report && report.clients) || []).flatMap((client) => client.items.map((item) => ({client, item})));
}

/** Los números del segmentado «Por qué pasa»: cuentas por causa y LÍNEAS con la advertencia. */
export function filterCounts(report) {
    const items = allItems(report).map(({item}) => item);
    const counts = {
        [FILTER_NO_LINE]: items.filter((item) => item.kind === KIND_NO_LINE).length,
        [FILTER_UNCOVERED]: items.filter((item) => item.kind === KIND_UNCOVERED).length,
        [FILTER_STALE]: items.reduce((total, item) => total + (isStale(item) ? item.stale_lines.length : 0), 0),
    };
    counts[FILTER_ALL] = counts[FILTER_NO_LINE] + counts[FILTER_UNCOVERED];
    return counts;
}

/** Los clientes con algún ítem que pasa el filtro, cada uno sólo con esos ítems. */
export function visibleClients(report, filter) {
    return ((report && report.clients) || [])
        .map((client) => ({...client, items: client.items.filter((item) => matchesFilter(item, filter))}))
        .filter((client) => client.items.length > 0);
}

/**
 * Los totales después de lo que el usuario resolvió en pantalla (`resolved`: `{itemKey: true}`) y de
 * las campañas que descartó una por una (`dismissedCampaigns`: `{itemKey: {campaignId: true}}`). La
 * pantalla no vuelve a pedir el informe por cada acción: lo descuenta, y la próxima carga trae el dato.
 */
export function adjustedTotals(report, resolved = {}, dismissedCampaigns = {}) {
    const totals = {...((report && report.totals) || {uncontrolled: 0, limit_only: 0})};
    allItems(report).forEach(({client, item}) => {
        const key = itemKey(client.client_id, item);
        const removed = resolved[key] ? item.amount_converted : removedCampaignsAmount(item, dismissedCampaigns[key]);
        if (!removed) return;
        const bucket = item.limit_only ? 'limit_only' : 'uncontrolled';
        totals[bucket] = Math.max(0, (totals[bucket] || 0) - removed);
    });
    return totals;
}

function removedCampaignsAmount(item, dismissed) {
    if (!dismissed || !item.amount || !item.amount_converted) return 0;
    const own = (item.campaigns || []).filter((row) => dismissed[row.campaign_id])
        .reduce((total, row) => total + (row.amount || 0), 0);
    // Las campañas vienen en la moneda de la cuenta: se pasan a la de la agencia con la misma
    // proporción que el total del ítem.
    return own * (item.amount_converted / item.amount);
}

/**
 * Las líneas de la cuenta que quien mira ve (`item.lines`). Un backend sin la clave (el de antes de UD-5)
 * manda sólo `item.line`: vale como la única.
 */
export function linesOf(item) {
    if (!item) return [];
    if (Array.isArray(item.lines)) return item.lines.filter(Boolean);
    return item.line ? [item.line] : [];
}

/** Las líneas a las que se pueden sumar campañas: las que eligen de una lista (`accepts_campaigns`). */
export function listLinesOf(item) {
    return linesOf(item).filter((line) => line.accepts_campaigns === true);
}

/**
 * Qué ofrece el botón principal de un ítem: sin línea, crearla; con una línea de lista, sumar directo; con
 * varias, elegir a cuál; con líneas pero ninguna de lista, editar la primera (su regla las deja afuera); y
 * si la cuenta no tiene ninguna línea que quien mira pueda ver, crear una.
 */
export function primaryAction(item) {
    if (item.kind === KIND_NO_LINE) return ACTION_CREATE_LINE;
    const lines = linesOf(item);
    if (!lines.length) return ACTION_CREATE_LINE;
    const listLines = listLinesOf(item);
    if (listLines.length === 1) return ACTION_ADD_TO_LINE;
    if (listLines.length > 1) return ACTION_CHOOSE_LINE;
    return ACTION_EDIT_LINE;
}

/** Los clientes de esas líneas, sin repetir: el nombre del cliente va en la opción sólo si hay más de uno. */
export function lineClientsOf(lines) {
    const names = [];
    (lines || []).forEach((line) => {
        const name = line && line.client_name;
        if (name && !names.includes(name)) names.push(name);
    });
    return names;
}

/** Las líneas cuyo nombre (o cliente) contiene lo buscado, sin distinguir mayúsculas ni tildes. */
export function filterLines(lines, query) {
    if (!normalizeText(query).trim()) return lines || [];
    return (lines || []).filter((line) => textIncludes(line.name, query) || textIncludes(line.client_name, query));
}

/**
 * Cuántas suma «Sumar las N»: todas las de la cuenta (`campaign_count`, aunque la tarjeta muestre menos)
 * menos las que el usuario descartó acá. Las suma el backend (`all_uncovered`), que ya conoce los
 * descartes: el botón promete lo que se va a hacer.
 */
export function addableCount(item, dismissed = {}) {
    const off = (item.campaigns || []).filter((row) => dismissed[row.campaign_id]).length;
    return Math.max(0, (item.campaign_count || 0) - off);
}

/** El equipo del cliente sin quien mira: a uno mismo no se le avisa. */
export function teammatesOf(team, myUserId) {
    return (team || []).filter((member) => String(member.id) !== String(myUserId));
}

/**
 * Una palabra de la campaña que más gasta, para el consejo «la línea puede incluir las campañas por su
 * nombre (por ejemplo, las que contienen «X»)». La primera de 3 letras o más; si no hay, el nombre.
 */
export function tipWord(item) {
    const first = ((item.campaigns || [])[0] || {}).name || '';
    const word = first.split(/[\s|_-]+/).find((part) => part.length >= 3);
    return word || first;
}

/** Cuántas cuentas sin cliente hay (`no_client.count`; desde UD-5 el bloque sólo cuenta, sin montos). */
export function noClientCount(report) {
    const block = report && report.no_client;
    return (block && Number(block.count)) || 0;
}

/** ¿El total del informe cerrado en pantalla quedó en cero? Es «Todo bajo control». */
export function isAllClear(report, totals) {
    if (!report) return false;
    const nothingElse = !(report.idle || []).length && !noClientCount(report);
    return !(totals.uncontrolled > 0) && !(totals.limit_only > 0) && visibleClients(report, FILTER_ALL).length === 0
        && nothingElse;
}
